import classNames from 'classnames';
import React, { ChangeEvent, ChangeEventHandler, ReactElement, useContext } from 'react';

import { IQATestableProps } from '../../shared/types/IQATestableProps';

import styles from './RadioButton.module.css';
import { IRadioGroupContext, RadioGroupContext } from './RadioGroupContext';

export type IRadioButtonProps = IQATestableProps & {
    value: any;
    id: string;
    disabled?: boolean,
    onChange?: ChangeEventHandler<HTMLInputElement>;
};

export function OzRadioButton (props: IRadioButtonProps): ReactElement {
    const {
        id,
        value,
        'data-testid': dataTestId,
        disabled = false,
        onChange,
    }: IRadioButtonProps = props;

    const group: IRadioGroupContext | undefined = useContext(RadioGroupContext);

    if (!group) {
        throw new Error('OzRadioButton must be a child of OzRadioGroup');
    }

    const selected: boolean = group.selectedValue === value;

    const hostClassNames: string = classNames({
        [styles.host]: true,
        [styles.selected]: selected,
        'border-gray-400 hover:border-teal-800': !selected && !disabled,
        'border-gray-200': disabled,
        'border-teal-800': selected,
    });

    let buttonTestId: string | undefined;

    if (dataTestId) {
        buttonTestId = dataTestId + '-button';
    }

    function handleChange (event: ChangeEvent<HTMLInputElement>): void {
        if (!event.target.checked || disabled) {
            return;
        }

        group?.setSelectedValue(value);

        if (onChange) {
            onChange(event);
        }
    }

    return (
        <div data-testid={dataTestId} className={`${hostClassNames} border rounded-full relative`}>
            <input
                id={id}
                type="radio"
                name={group.name}
                onChange={handleChange}
                aria-checked={selected}
                checked={selected}
                data-testid={buttonTestId}
                disabled={disabled}
                className="absolute top-0 left-0 w-full h-full opacity-00"
            />
        </div>
    );
}
